import React, { useMemo } from 'react';

import { Button, Flex, Space, Table, type TableColumnsType } from 'antd';
import DeleteSeverity from 'src/pages/severity/ListSeverity/components/DeleteSeverity';
import EditSeverity from 'src/pages/severity/ListSeverity/components/EditSeverity';
import { SeverityColorCell } from 'src/pages/severity/ListSeverity/styles';
import { useGetSeveritiesQuery } from 'src/redux/services/centralApi';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { Severity } from 'src/interfaces/entities/severity';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const boolFilterConfig = [
	{ text: 'Sim', value: true, },
	{ text: 'Não', value: false, },
];

export default function ListSeverityPage() {
	const navigate = useNavigate();
	const {
		data: severitysData,
		isLoading: isLoadingSeveritys,
		isFetching: isFetchingSeveritys,
		refetch: refetchSeveritys,
	} = useGetSeveritiesQuery();

	const columns = useMemo<TableColumnsType<Severity>>(() => [
		{
			dataIndex: 'color',
			title: 'Cor',
			key: 'color',
			width: 80,
			render: (value: string) => (
				<SeverityColorCell style={{ backgroundColor: value, }} />
			),
		},
		{
			dataIndex: 'name',
			title: 'Nome',
			key: 'name',
		},
		{
			dataIndex: 'in_zapi',
			title: 'Whatsapp',
			key: 'in_zapi',
			filters: boolFilterConfig,
			onFilter: (value, record) => record.in_zapi === value,
			render: (value: boolean) => value ? 'Sim' : 'Não',
		},
		{
			dataIndex: 'in_telegram',
			title: 'Telegram',
			key: 'in_telegram',
			filters: boolFilterConfig,
			onFilter: (value, record) => record.in_telegram === value,
			render: (value: boolean) => value ? 'Sim' : 'Não',
		},
		{
			dataIndex: 'in_dashboard',
			title: 'Dashboard',
			key: 'in_dashboard',
			filters: boolFilterConfig,
			onFilter: (value, record) => record.in_dashboard === value,
			render: (value: boolean) => value ? 'Sim' : 'Não',
		},
		{
			dataIndex: 'in_webhook',
			title: 'Webhook',
			key: 'in_webhook',
			filters: boolFilterConfig,
			onFilter: (value, record) => record.in_webhook === value,
			render: (value: boolean) => value ? 'Sim' : 'Não',
		},
		{
			dataIndex: 'is_active',
			title: 'Ativo',
			key: 'is_active',
			filters: [
				{ text: 'Ativo', value: true, },
				{ text: 'Inativo', value: false, },
			],
			onFilter: (value, record) => record.is_active === value,
			render: (value: boolean) => value ? 'Ativo' : 'Inativo',
		},
		{
			dataIndex: 'creation_date',
			title: 'Data de criação',
			key: 'creation_date',
			sorter: (a, b) => {
				const dateA = a.creation_date ? dayjs(a.creation_date) : null;
				const dateB = b.creation_date ? dayjs(b.creation_date) : null;

				if (dateA && dateB && dateA.isValid() && dateB.isValid()) {
					return dateA.diff(dateB);
				}
				if (!dateA && !dateB) return 0;
				if (!dateA) return 1;
				if (!dateB) return -1;
				return 0;
			},
		},
		{
			title: 'Ações',
			key: 'actions',
			render: (_, record) => (
				<Space>
					<DeleteSeverity row={record} />
					<EditSeverity row={record} />
				</Space>
			),
		},
	], []);

	return (
		<DashboardTemplate pageTitle="Gerenciar Severidades">
			<Flex style={{ padding: 5, marginBottom: 8, }} gap={8} justify="end">
				<Button
					type="primary"
					onClick={() => navigate('/severidade/adicionar')}
					icon={<PlusOutlined />}
				>
Adicionar
				</Button>
				<Button
					type="text"
					onClick={refetchSeveritys}
					icon={<ReloadOutlined />}
				/>
			</Flex>
			<Table<Severity>
				columns={columns}
				dataSource={severitysData ?? []}
				rowKey="severity_id"
				loading={isLoadingSeveritys || isFetchingSeveritys}
				size="small"
			/>
		</DashboardTemplate>
	);
}
